import { create } from 'zustand';
import type {
  BookingState,
  BookingConfirmation} from '@/shared/types/domain';
import {
  BookingStep
} from '@/shared/types/domain';

/**
 * Booking Store - Pure Wizard UI State
 * Manages ONLY the booking wizard flow state:
 * - Current step in the wizard
 * - Form inputs and selections
 * - UI state (loading, errors, confirmation)
 *
 * Note: Shop data and availability come from React Query hooks, NOT this store
 */
interface BookingUIStore {
  // UI State
  activeStep: BookingStep;
  isLoading: boolean;
  error: string | null;
  bookingConfirmation: BookingConfirmation | null;
  bookingState: BookingState;

  // UI Actions
  setActiveStep: (step: BookingStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setBookingConfirmation: (confirmation: BookingConfirmation | null) => void;

  // State update methods
  updateBookingState: (updates: Partial<BookingState>) => void;
  resetBookingState: (shopId: string) => void;
}

export const useBookingStore = create<BookingUIStore>(set => ({
  // Initial state
  activeStep: BookingStep.PROVIDER,
  isLoading: false,
  error: null,
  bookingConfirmation: null,
  bookingState: {
    shopId: '',
    selectedServices: [],
    selectedProvider: null,
    selectedDate: null,
    selectedTimeSlot: null,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    notes: '',
    acceptedLegalTerms: false,
  },

  // UI Actions
  setActiveStep: step => set({ activeStep: step }),
  nextStep: () =>
    set(state => ({
      activeStep: Math.min(state.activeStep + 1, BookingStep.REVIEW_AND_PAY),
    })),
  prevStep: () =>
    set(state => ({
      activeStep: Math.max(state.activeStep - 1, BookingStep.PROVIDER),
    })),
  setIsLoading: isLoading => set({ isLoading }),
  setError: error => set({ error }),
  setBookingConfirmation: confirmation =>
    set({ bookingConfirmation: confirmation }),

  // State update methods
  updateBookingState: updates =>
    set(state => ({
      bookingState: { ...state.bookingState, ...updates },
    })),

  resetBookingState: shopId =>
    set({
      activeStep: BookingStep.PROVIDER,
      bookingConfirmation: null,
      error: null,
      bookingState: {
        shopId,
        selectedServices: [],
        selectedProvider: null,
        selectedDate: null,
        selectedTimeSlot: null,
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        notes: '',
        acceptedLegalTerms: false,
      },
    }),
}));
